import { useHandler } from "@redotech/react-util/hook";
import {
  rcoProductSkuSegmentSchema,
  RcoProductSkuSegmentSchema,
  RcoSegmentStep,
} from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import {
  SegmentListMultipleItemMembershipOperator,
  SegmentListSingleItemMembershipOperator,
} from "@redotech/redo-model/checkouts/checkout-tree";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoTagInputField } from "@redotech/redo-web/arbiter-components/input/redo-tag-input-field";
import { OperatorSelector } from "@redotech/redo-web/conversion/operator-selector";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo, useMemo, useState } from "react";
import { PartialWithRequiredType } from "../..";

export const ProductSkuInspectorContent = memo(
  function ProductSkuInspectorContent({ step }: { step: RcoSegmentStep }) {
    const segment = useMemo(
      () => rcoProductSkuSegmentSchema.parse(step.segment),
      [step],
    );

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Condition
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            {operatorsToText(segment.cartOperator, segment.operator)}
          </Text>
        </Flex>
        <Flex dir="row" gap="md" wrap="wrap">
          {segment.skus.map((sku, index) => (
            <RedoBadge color="gray" key={index} size="sm" text={sku} />
          ))}
        </Flex>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          {segment.requireExactMatch
            ? "Requires exact match"
            : "Does not require exact match"}
        </Text>
      </>
    );
  },
);

const operatorsToText = (
  cartOperator: RcoProductSkuSegmentSchema["cartOperator"],
  operator: RcoProductSkuSegmentSchema["operator"],
) => {
  switch (cartOperator) {
    case SegmentListMultipleItemMembershipOperator.ANY_IN:
      switch (operator) {
        case SegmentListSingleItemMembershipOperator.IN:
          return "The SKU for at least one product in the cart is in the list:";
        case SegmentListSingleItemMembershipOperator.NOT_IN:
          return "The SKU for at least one product in the cart is not in the list:";
        default:
          assertNever(operator);
      }
      break;
    case SegmentListMultipleItemMembershipOperator.ALL_IN:
      switch (operator) {
        case SegmentListSingleItemMembershipOperator.IN:
          return "The SKU for every product in the cart is in the list:";
        case SegmentListSingleItemMembershipOperator.NOT_IN:
          return "The SKU for every product in the cart is not in the list:";
        default:
          assertNever(operator);
      }
      break;
    case SegmentListMultipleItemMembershipOperator.NONE_IN:
      switch (operator) {
        case SegmentListSingleItemMembershipOperator.IN:
          return "The SKU for every product in the cart is not in the list:";
        case SegmentListSingleItemMembershipOperator.NOT_IN:
          return "The SKU for every product in the cart is in the list:";
        default:
          assertNever(operator);
      }
      break;
    default:
      assertNever(cartOperator);
  }
};

export const segmentListMultipleItemMembershipOperatorToText: Record<
  SegmentListMultipleItemMembershipOperator,
  string
> = {
  [SegmentListMultipleItemMembershipOperator.ANY_IN]: "at least one",
  [SegmentListMultipleItemMembershipOperator.NONE_IN]: "no",
  [SegmentListMultipleItemMembershipOperator.ALL_IN]: "every",
};

export const segmentListSingleItemMembershipOperatorToText: Record<
  SegmentListSingleItemMembershipOperator,
  string
> = {
  [SegmentListSingleItemMembershipOperator.IN]: "is",
  [SegmentListSingleItemMembershipOperator.NOT_IN]: "is not",
};

export const EditableProductSkuInspectorContent = memo(
  function EditableProductSkuInspectorContent({
    step,
    setStep,
  }: {
    step: Omit<RcoSegmentStep, "matchingNextId" | "nonMatchingNextId"> & {
      matchingNextId?: string | undefined;
      nonMatchingNextId?: string | undefined;
    };
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  }) {
    const [skuInput, setSkuInput] = useState("");

    const segment = useMemo(
      () => rcoProductSkuSegmentSchema.parse(step.segment),
      [step],
    );

    const skus = useMemo(
      () => segment.skus.map((sku) => ({ text: sku })),
      [segment.skus],
    );

    const handleSetSkus = useHandler((tags: string[]) => {
      setStep({ ...step, segment: { ...segment, skus: tags } });
    });

    const handleCartOperatorSelected = useHandler(
      (cartOperator: SegmentListMultipleItemMembershipOperator) => {
        const updatedSegment = { ...segment, cartOperator };
        setStep({ ...step, segment: updatedSegment });
      },
    );

    const handleOperatorSelected = useHandler(
      (operator: SegmentListSingleItemMembershipOperator) => {
        const updatedSegment = { ...segment, operator };
        setStep({ ...step, segment: updatedSegment });
      },
    );

    const handleChangeRequireExactMatch = useHandler(
      (requireExactMatch: boolean) => {
        const updatedSegment = { ...segment, requireExactMatch };
        setStep({ ...step, segment: updatedSegment });
      },
    );

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Condition
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            The SKU for
          </Text>
        </Flex>
        <Flex>
          <OperatorSelector<SegmentListMultipleItemMembershipOperator>
            availableOptions={Object.values(
              SegmentListMultipleItemMembershipOperator,
            )}
            onOperatorSelected={handleCartOperatorSelected}
            operatorToText={segmentListMultipleItemMembershipOperatorToText}
            selectedOperator={segment.cartOperator}
          />
        </Flex>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          product in the cart
        </Text>
        <Flex>
          <OperatorSelector<SegmentListSingleItemMembershipOperator>
            availableOptions={Object.values(
              SegmentListSingleItemMembershipOperator,
            )}
            onOperatorSelected={handleOperatorSelected}
            operatorToText={segmentListSingleItemMembershipOperatorToText}
            selectedOperator={segment.operator}
          />
        </Flex>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          in the list:
        </Text>
        <Flex w="full">
          <RedoTagInputField
            fitContent
            inputValue={skuInput}
            placeholder="Type a SKU and press enter"
            setInputValue={setSkuInput}
            setTags={handleSetSkus}
            tags={skus}
          />
        </Flex>
        <RedoCheckbox
          label="Require exact match"
          setValue={handleChangeRequireExactMatch}
          size="xs"
          value={segment.requireExactMatch}
        />
      </>
    );
  },
);
